/*
 * Copyright (c) 2020 Via Transportation, Inc. All rights reserved.
 *
 * This software is distributed under license and may not be used, copied,
 * modified, or distributed without the express written permission of
 * Via Transportation, Inc.
 */

import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import weekData from "cldr-core/supplemental/weekData.json";
import { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";

export const usePrevious = <T>(value: T) => {
    const ref = useRef<T>();
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
};

export const useCommonState = () => useSelector<any, any>((state) => state);

export const useLarge = () => useMediaQuery(useTheme().breakpoints.up("lg"));
export const useMobile = () => useMediaQuery(useTheme().breakpoints.down("md"));

export const useFirstDay = () => {
    const { configuration } = useCommonState();
    const { clientRegion } = configuration;
    const days = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

    if (!clientRegion) {
        return 1;
    }

    if (clientRegion === "US") {
        return 1;
    }

    return days.indexOf((weekData.supplemental.weekData.firstDay as Record<string, string>)[clientRegion]) as
        | 0
        | 1
        | 2
        | 3
        | 4
        | 5
        | 6;
};

export const useSearchParameters = (search: string) => {
    const query = search.substring(1);
    const vars = query.split("&");
    return vars.reduce((acc, curr) => {
        const pair = curr.split("=");
        const key = decodeURIComponent(pair[0]);
        const val = decodeURIComponent(pair[1]);
        if (key) acc[key] = val;
        return acc;
    }, {} as { [key: string]: string });
};

export const useHiddenInput = () => {
    if (document.getElementById("hiddenInput") === null) {
        const input = document.createElement("input");
        input.setAttribute("id", "hiddenInput");
        input.setAttribute("readonly", "readonly");
        input.setAttribute("type", "text");
        input.style.position = "absolute";
        input.style.opacity = "0";
        input.style.height = "-999px";
        input.style.fontSize = "16px";
        document.body.prepend(input);
    }
    const hiddenInput = document.getElementById("hiddenInput") as HTMLInputElement;
    return useMemo(
        () => [
            () => {
                hiddenInput.focus();
            },
            () => {
                hiddenInput.blur();
                document.body.removeChild(hiddenInput);
            },
        ],
        [hiddenInput]
    );
};

export const getCookieValueByName = (cookieName: string) => {
    const name = cookieName + "=";
    const ca = document.cookie.split(";");
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === " ") {
            c = c.substring(1);
        }
        if (c.indexOf(name) === 0) {
            return c.substring(name.length, c.length);
        }
    }
    return "";
};

export const getCSRFTokenHeader = () => {
    const token = getCookieValueByName("csrf-token");
    return {
        ...(token && {
            "X-CSRF-TOKEN": token as string,
        }),
    };
};
